// 테넌트 테마 색상 유틸리티
// 메인/서브 색상에서 파생 색상(대비 글자색, hover, 연한 배경 등)을 자동 계산한다.
// 관리자가 아주 밝거나 어두운 색을 골라도 버튼 글자/hover가 항상 읽히도록 보장.

const DARK_TEXT = '#1f2937'; // slate-800: 밝은 배경 위 글자색
const LIGHT_TEXT = '#ffffff';

export const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (/^[a-f\d]{3}$/i.test(h)) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (!/^[a-f\d]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) => {
  const to2 = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
};

// 인지 밝기 (0~255)
const yiq = ({ r, g, b }) => (r * 299 + g * 587 + b * 114) / 1000;

// ratio(0~1)만큼 다른 색과 혼합
const mix = (rgb, target, ratio) => ({
  r: rgb.r + (target.r - rgb.r) * ratio,
  g: rgb.g + (target.g - rgb.g) * ratio,
  b: rgb.b + (target.b - rgb.b) * ratio,
});

const BLACK = { r: 0, g: 0, b: 0 };
const WHITE = { r: 255, g: 255, b: 255 };

// WCAG 상대 휘도(0~1) 및 대비비 — 글자 가독성 판단용 (단순 밝기보다 정확)
const channelLum = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};
const relLuminance = ({ r, g, b }) =>
  0.2126 * channelLum(r) + 0.7152 * channelLum(g) + 0.0722 * channelLum(b);
const contrastRatio = (lum1, lum2) => {
  const hi = Math.max(lum1, lum2);
  const lo = Math.min(lum1, lum2);
  return (hi + 0.05) / (lo + 0.05);
};
const DARK_TEXT_LUM = relLuminance(hexToRgb(DARK_TEXT));

// 배경색 위 글자색: 어두운 글자/흰 글자 중 실제 명도 대비가 더 높은 쪽 선택.
// 단순 밝기 임계값과 달리 채도 높은 색(밝은 초록 등)에서도 항상 잘 읽히는 쪽을 고른다.
export const getContrastText = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return LIGHT_TEXT;
  const bgLum = relLuminance(rgb);
  const darkContrast = contrastRatio(bgLum, DARK_TEXT_LUM);
  const lightContrast = contrastRatio(bgLum, 1); // 흰색 휘도 = 1
  return darkContrast >= lightContrast ? DARK_TEXT : LIGHT_TEXT;
};

// hover 셰이드: 보통은 약간 어둡게, 아주 어두운 색은 약간 밝게 (변화가 보이도록)
export const getHoverShade = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return yiq(rgb) < 60 ? rgbToHex(mix(rgb, WHITE, 0.14)) : rgbToHex(mix(rgb, BLACK, 0.12));
};

// 연한 배경 틴트 (배지, 강조 박스 등)
export const getSoftTint = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(mix(rgb, WHITE, 0.92));
};

// 흰 배경 위 텍스트로 써도 읽히는 버전.
// 흰색 대비가 4.5:1(WCAG AA) 이상이 될 때까지 어둡게 (이미 충분히 진하면 원색 유지).
export const getReadableOnWhite = (hex) => {
  let rgb = hexToRgb(hex);
  if (!rgb) return hex;
  let guard = 0;
  while (contrastRatio(relLuminance(rgb), 1) < 4.5 && guard < 20) {
    rgb = mix(rgb, BLACK, 0.1);
    guard += 1;
  }
  return rgbToHex(rgb);
};

// 메인/서브 색상으로 CSS 변수 전체 세트 생성
const THEME_PREFIXES = ['primary', 'secondary'];
const THEME_SUFFIXES = ['color', 'rgb', 'contrast', 'hover', 'soft', 'strong'];

export const buildThemeVars = (primary, secondary) => {
  const vars = {};
  const addColorSet = (prefix, hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return;
    vars[`--${prefix}-color`] = hex;
    // 공백 구분. Tailwind 의 rgb(var(--*-rgb) / <alpha-value>) 문법이 요구하는 형태다.
    // 콤마 구분이면 rgb(19, 236, 91 / 1) 이 되어 무효 CSS → bg/text/border-primary 가 전부 죽는다.
    vars[`--${prefix}-rgb`] = `${Math.round(rgb.r)} ${Math.round(rgb.g)} ${Math.round(rgb.b)}`;
    vars[`--${prefix}-contrast`] = getContrastText(hex);
    vars[`--${prefix}-hover`] = getHoverShade(hex);
    vars[`--${prefix}-soft`] = getSoftTint(hex);
    vars[`--${prefix}-strong`] = getReadableOnWhite(hex);
  };
  addColorSet('primary', primary);
  addColorSet('secondary', secondary);
  return vars;
};

// 테넌트 테마 변수를 엘리먼트(보통 documentElement)에 주입
export const applyThemeVars = (el, primary, secondary) => {
  if (!el) return;
  const vars = buildThemeVars(primary, secondary);
  Object.entries(vars).forEach(([name, val]) => el.style.setProperty(name, val));
};

// 인라인으로 덮어쓴 테마 변수를 제거해 index.css :root 기본값으로 복귀.
// (관리자 화면 등 테넌트 테마를 적용하면 안 되는 화면에서 사용)
export const clearThemeVars = (el) => {
  if (!el) return;
  THEME_PREFIXES.forEach((p) =>
    THEME_SUFFIXES.forEach((s) => el.style.removeProperty(`--${p}-${s}`))
  );
};
